/**
 * Custom line-art service icons, drawn to match the approved design where
 * lucide-react has no equivalent (it ships no stove/range, pergola, combined
 * window-and-door, or hand-saw). Each is authored in lucide's exact conventions — 24x24 viewBox,
 * `fill="none"`, `stroke="currentColor"`, `strokeWidth` 2, round caps/joins —
 * so it sits consistently next to the lucide icons used for the other services.
 * Props spread last, so a caller's `className` / `strokeWidth` win.
 */
import type { ReactNode, SVGProps } from "react";

function IconBase({
  children,
  ...props
}: SVGProps<SVGSVGElement> & { children: ReactNode }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={24}
      height={24}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      {children}
    </svg>
  );
}

/** Kitchen Remodeling — a range/cooktop with an overhead vent hood. */
export function KitchenRangeIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase {...props}>
      {/* vent hood */}
      <path d="M5 3h14l-1.5 4h-11z" />
      {/* range body */}
      <rect x="4" y="9" width="16" height="12" rx="1.5" />
      {/* cooktop divider */}
      <path d="M4 13h16" />
      {/* burners */}
      <circle cx="9" cy="11" r="1" />
      <circle cx="15" cy="11" r="1" />
      {/* oven door handle */}
      <path d="M8.5 16.5h7" />
    </IconBase>
  );
}

/** Windows & Doors — a paned window beside a door on a ground line. */
export function WindowDoorIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase {...props}>
      {/* ground line */}
      <path d="M3 21h18" />
      {/* window */}
      <rect x="4" y="6" width="6.5" height="8" rx="0.5" />
      <path d="M7.25 6v8M4 10h6.5" />
      {/* door */}
      <path d="M13.5 21V4.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 .5.5V21" />
      {/* door knob */}
      <circle cx="15.5" cy="12.5" r="0.6" />
    </IconBase>
  );
}

/** Custom Carpentry — a hand saw: toothed blade and a D-grip handle. */
export function CarpentryIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase {...props}>
      {/* blade: back edge to handle, then toothed cutting edge back to the tip */}
      <path d="M4 10 15 6 15 12 13.8 10.6 12.8 11.6 11.6 10.2 10.6 11.2 9.4 9.8 8.4 10.8 7.2 9.4 6.2 10.4 5 9Z" />
      {/* handle grip */}
      <path d="M15 6c2.4 0 3.8 1.3 3.8 3s-1.4 3-3.8 3" />
    </IconBase>
  );
}

/** Decks — a pergola: two posts, top beams, cross rafters. */
export function PergolaIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase {...props}>
      {/* posts */}
      <path d="M6 21V9M18 21V9" />
      {/* beams */}
      <path d="M4 9h16M4 6h16" />
      {/* rafters */}
      <path d="M9 4v6M12 4v6M15 4v6" />
    </IconBase>
  );
}
